import { z } from 'zod'

/**
 * Kubernetes product tier types
 */
export const KubernetesTier = z.enum(['namespace', 'cluster', 'addon'])
export type KubernetesTier = z.infer<typeof KubernetesTier>

/**
 * Kubernetes addon types
 */
export const KubernetesAddonType = z.enum(['storage', 'database', 'backup'])
export type KubernetesAddonType = z.infer<typeof KubernetesAddonType>

/**
 * Base features for all Kubernetes products
 */
export const KubernetesFeaturesBase = z.object({
  type: z.literal('kubernetes'),
  tier: KubernetesTier,
  popular: z.boolean().optional()
})

/**
 * Features specific to fixed-tier Namespace products
 */
export const KubernetesNamespaceFeatures = KubernetesFeaturesBase.extend({
  tier: z.literal('namespace'),
  cpu: z.string().optional(), // e.g., "1 vCPU", "2 vCPUs"
  ram: z.string().optional(), // e.g., "1 GiB", "2 GiB"
  billing: z.string().optional(), // "payg" for pay-as-you-go, absent for fixed
  includedEgressGiB: z.number().optional()
})

export type KubernetesNamespaceFeatures = z.infer<typeof KubernetesNamespaceFeatures>

/**
 * Features specific to PAYG Namespace products
 */
export const KubernetesPaygFeatures = KubernetesFeaturesBase.extend({
  tier: z.literal('namespace'),
  billing: z.literal('payg')
})

export type KubernetesPaygFeatures = z.infer<typeof KubernetesPaygFeatures>

/**
 * Features specific to Cluster products
 */
export const KubernetesClusterFeatures = KubernetesFeaturesBase.extend({
  tier: z.literal('cluster'),
  controlPlane: z.string().optional(), // e.g., "Dedicated", "Shared"
  workers: z.number().optional(), // Number of workers included
  nodeType: z.string().optional(), // e.g., "worker"
  server: z.string().optional(), // e.g., "Ax42"
  isolation: z.string().optional(), // e.g., "full"
  compliance: z.boolean().optional()
})

export type KubernetesClusterFeatures = z.infer<typeof KubernetesClusterFeatures>

/**
 * Features specific to Addon products
 */
export const KubernetesAddonFeatures = KubernetesFeaturesBase.extend({
  tier: z.literal('addon'),
  addonType: KubernetesAddonType,
  // Storage addon
  capacity: z.string().optional(), // e.g., "50 GiB"
  accessMode: z.string().optional(), // e.g., "RWX"
  // Database addon
  databases: z.array(z.string()).optional(), // e.g., ["postgres", "redis"]
  managed: z.boolean().optional(),
  dedicated: z.boolean().optional(),
  highAvailability: z.boolean().optional(),
  nodes: z.number().optional(),
  // Backup addon
  unit: z.string().optional(), // e.g., "10 GiB"
  usageBased: z.boolean().optional()
})

export type KubernetesAddonFeatures = z.infer<typeof KubernetesAddonFeatures>

/**
 * Union of all Kubernetes feature types
 */
export const KubernetesFeatures = z.union([
  KubernetesNamespaceFeatures,
  KubernetesPaygFeatures,
  KubernetesClusterFeatures,
  KubernetesAddonFeatures
])

export type KubernetesFeatures = z.infer<typeof KubernetesFeatures>

/**
 * Type guard to check if features are Kubernetes features
 */
export function isKubernetesFeatures(
  features: Record<string, unknown> | undefined
): features is KubernetesFeatures {
  if (!features) return false
  return features.type === 'kubernetes'
}

/**
 * Type guard for namespace features
 */
export function isNamespaceFeatures(
  features: Record<string, unknown> | undefined
): features is KubernetesNamespaceFeatures {
  if (!features) return false
  return features.type === 'kubernetes' && features.tier === 'namespace'
}

/**
 * Type guard for cluster features
 */
export function isClusterFeatures(
  features: Record<string, unknown> | undefined
): features is KubernetesClusterFeatures {
  if (!features) return false
  return features.type === 'kubernetes' && features.tier === 'cluster'
}

/**
 * Type guard for PAYG namespace features
 */
export function isPaygFeatures(
  features: Record<string, unknown> | undefined
): features is KubernetesPaygFeatures {
  if (!features) return false
  return features.type === 'kubernetes' && features.tier === 'namespace' && features.billing === 'payg'
}

/**
 * Type guard for addon features
 */
export function isAddonFeatures(
  features: Record<string, unknown> | undefined
): features is KubernetesAddonFeatures {
  if (!features) return false
  return features.type === 'kubernetes' && features.tier === 'addon'
}
